import React, { Component, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-grove-50 flex items-center justify-center p-8">
          <div className="bg-white rounded-xl border border-grove-300 shadow-sm max-w-md w-full p-8 text-center">
            <AlertTriangle className="w-10 h-10 text-honey-600 mx-auto mb-4" />
            <h2 className="font-display text-lg font-semibold text-bark-800 mb-2">
              Something went wrong
            </h2>
            <p className="text-sm text-bark-500 mb-6">
              An unexpected error occurred. Your data is safe.
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-forest-600 text-white hover:bg-forest-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
